import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/users/${userId}`).then((r) => setProfile(r.data)).catch(() => setProfile(false));
  }, [userId]);

  if (profile === null) return <div className="py-32 flex justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>;
  if (profile === false) return <div className="py-32 text-center text-muted-foreground">User not found.</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <div className="bg-white border border-black/10 rounded-2xl p-6 flex items-center gap-5">
        <Avatar className="w-20 h-20 border border-black/10">
          <AvatarImage src={profile.picture} />
          <AvatarFallback className="bg-[#E3E8E1] text-2xl font-semibold">{profile.name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <h1 data-testid="profile-name" className="font-display text-3xl font-bold tracking-tight">{profile.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            {profile.review_count > 0 ? (
              <span data-testid="profile-rating" className="flex items-center gap-1 font-semibold">
                <Star className="w-4 h-4 fill-[#FF5A00] text-[#FF5A00]" /> {profile.rating}
                <span className="text-muted-foreground font-normal">({profile.review_count} rating{profile.review_count > 1 ? "s" : ""})</span>
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">No ratings yet</span>
            )}
            <span className="flex items-center gap-1 text-sm text-[#2E7D32]"><ShieldCheck className="w-4 h-4" /> Verified member</span>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-8">
        <h2 className="font-display text-2xl font-bold tracking-tight">Trust reviews</h2>
        {profile.reviews.length === 0 ? (
          <p className="text-muted-foreground text-sm mt-3">This member hasn't received any reviews yet.</p>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {profile.reviews.map((r) => (
              <div key={r.id} data-testid={`user-review-${r.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9"><AvatarImage src={r.rater_picture} /><AvatarFallback className="bg-[#E3E8E1] text-sm">{r.rater_name?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-semibold text-sm">{r.rater_name}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{r.context === "rental" ? "Rental" : "Sale"}</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-black/15"}`} />)}</div>
                </div>
                <p className="mt-2 text-sm">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Listings */}
      {profile.listings.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl font-bold tracking-tight">{profile.name}'s listings</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {profile.listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
